import { useState } from 'react'
import type { FormEvent } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ApiError } from '../api/client'
import { requestLoginOtp, verifyLoginOtp } from '../api/auth'
import {
  AUTH_MESSAGES,
  AUTH_PATHS,
  type AuthPendingAction,
} from '../constants/auth'
import { toE164Phone } from '../helpers/auth'
import { resolveAuthRedirect } from '../helpers/authRedirect'
import { useAuth } from './useAuth'

export function useSignupForm() {
  const navigate = useNavigate()
  const location = useLocation()
  const { applyTokens } = useAuth()
  const [mobile, setMobile] = useState('')
  const [otp, setOtp] = useState('')
  const [otpSent, setOtpSent] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState<AuthPendingAction>(null)

  const submitting = pending !== null

  async function finishWithTokens(
    tokens: Awaited<ReturnType<typeof verifyLoginOtp>>,
    successMessage: string,
  ) {
    applyTokens(tokens)
    setMessage(successMessage)
    setError(null)
    navigate(resolveAuthRedirect(location.state, AUTH_PATHS.home), {
      replace: true,
    })
  }

  async function onSendOtp() {
    setPending('otp-request')
    setError(null)
    setMessage(null)
    try {
      await requestLoginOtp(toE164Phone(mobile))
      setOtpSent(true)
      setMessage(AUTH_MESSAGES.otpSent)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to send OTP')
    } finally {
      setPending(null)
    }
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setError(null)
    setMessage(null)
    try {
      if (!otpSent) {
        setPending('otp-request')
        await requestLoginOtp(toE164Phone(mobile))
        setOtpSent(true)
        setMessage(AUTH_MESSAGES.otpSent)
        return
      }

      setPending('otp-verify')
      const tokens = await verifyLoginOtp(toE164Phone(mobile), otp)
      await finishWithTokens(tokens, AUTH_MESSAGES.registered)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Signup failed')
    } finally {
      setPending(null)
    }
  }

  return {
    mobile,
    setMobile,
    otp,
    setOtp,
    otpSent,
    message,
    error,
    pending,
    submitting,
    onSubmit,
    onSendOtp,
  }
}
